import React from 'react';
import { Link } from 'react-router-dom';
import Canonical from './Canonical';

function Privacy() {
  return (
    <>
      <Canonical path="/privacy" />
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div className="text-xl font-bold text-primary">
          <Link to="/" className="text-primary no-underline">Remote Assistant</Link>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/" className="text-gray-700 hover:text-primary no-underline">Home</Link>
          <Link to="/signup" className="px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover text-white font-semibold shadow no-underline">
            Get Started
          </Link>
        </div>
      </nav>

      <section className="pt-24 pb-16 px-8 max-w-4xl mx-auto">
        <div className="bg-white p-12 rounded-2xl shadow-sm border">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">Privacy Policy</h1>
          <p className="text-center text-gray-600 mb-12">
            <strong>Effective Date:</strong> April 18, 2026
          </p>
          
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">1. AI System Disclosure (EU AI Act Compliance)</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                This service utilizes Artificial Intelligence to process communications. In accordance with the EU AI Act, we ensure that users are notified they are interacting with an automated system.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Your customers will be informed when they interact with our AI Assistant, ensuring full transparency about the automated nature of the service.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect information you provide directly, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li><strong>Account Information:</strong> Name, email address, phone number, business name, and billing details when you create an account.</li>
                <li><strong>Communication Data:</strong> Voice recordings, call transcripts, SMS messages, and email content processed by your AI assistant during customer interactions.</li>
                <li><strong>Business Configuration:</strong> Service offerings, pricing, staff details, availability schedules, and other settings you configure for your assistant.</li>
                <li><strong>Google User Data:</strong> When you authorize Google integrations, we access specific Google data as described in Section 3 below.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">3. Google User Data</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Remote Assistant's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google API Services User Data Policy</a>, including the Limited Use requirements.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.1 Data Accessed</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                When you connect your Google account, we request access to the following data depending on the integrations you enable:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li><strong>Google Calendar</strong> (calendar scope): We read and write calendar events to manage appointment bookings, check staff availability, and synchronize scheduling for your AI assistant.</li>
                <li><strong>Google Sheets</strong> (spreadsheets scope): We read and write to spreadsheets that we create on your behalf for data export, reporting, and business record-keeping.</li>
                <li><strong>Google Drive</strong> (drive.file scope): We access only files that our application creates. We do not access your existing Google Drive files.</li>
                <li><strong>Gmail</strong> (gmail.send, gmail.readonly scopes): We send emails on your behalf (e.g., booking confirmations, follow-ups) and read incoming emails to enable your AI assistant to respond to customer inquiries via email.</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.2 How We Use Google Data</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Google user data is used exclusively to provide the features you have enabled:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li>Calendar data is used to check real-time availability and create or modify bookings on behalf of your customers.</li>
                <li>Sheets data is used to export and organize business records you have requested.</li>
                <li>Drive access is limited to files our application creates for your account.</li>
                <li>Gmail access is used to send transactional emails (booking confirmations, reminders) and to read inbound customer emails so your AI assistant can respond.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>We do not use Google user data for advertising, market research, or any purpose unrelated to providing and improving our core AI assistant functionality for your account.</strong>
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.3 How We Store Google Data</h3>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li>OAuth tokens (access and refresh tokens) are stored encrypted in AWS DynamoDB with AES-256 encryption at rest and are accessible only to your account.</li>
                <li>Calendar event data is cached temporarily (up to 24 hours) in Redis for real-time availability checks, then automatically expires.</li>
                <li>We do not persistently store the contents of your emails or full calendar event details beyond what is required for active assistant operations.</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.4 How We Share Google Data</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We do not sell, rent, or share your Google user data with third parties except:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li><strong>AI Processing:</strong> Calendar availability and email content may be provided to our AI language model providers (OpenAI, Groq) as context during active customer conversations, solely to generate relevant responses. This data is not retained by these providers for training purposes.</li>
                <li><strong>Infrastructure Providers:</strong> Data is processed through AWS (hosting, database), Redis Cloud (caching), and Deepgram (speech-to-text) as necessary sub-processors. These providers process data under strict data processing agreements.</li>
                <li><strong>Legal Requirements:</strong> We may disclose data if required by law, regulation, or valid legal process.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>We do not share Google user data with any other third parties, and we do not use it for purposes beyond those described in this policy.</strong>
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.5 Revoking Google Access</h3>
              <p className="text-gray-700 leading-relaxed">
                You can disconnect your Google account at any time from your Remote Assistant dashboard. You can also revoke access directly from your{' '}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Account permissions page</a>. Upon revocation, we delete your stored OAuth tokens and cease all access to your Google data. Cached data expires automatically within 24 hours.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">4. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li>Provide and operate AI assistant services for voice, SMS, and email communication.</li>
                <li>Manage appointment scheduling and staff coordination.</li>
                <li>Process payments and maintain billing records.</li>
                <li>Improve service quality and reliability.</li>
                <li>Send service-related notifications and updates.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">5. Data Sovereignty & AI Training</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>We do not use your proprietary business data, customer voice recordings, or Google user data to train our foundational models for other clients.</strong> Your data remains siloed and is used exclusively for your assistant's performance.
              </p>
              <p className="text-gray-700 leading-relaxed">
                All customer interactions and business-specific information are kept separate and confidential, ensuring your competitive advantage is protected.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">6. Human-in-the-Loop Control</h2>
              <p className="text-gray-700 leading-relaxed">
                While the AI operates autonomously, you (the Deployer) retain the right to review conversation logs, monitor AI decisions, and override automated responses at any time through your dashboard. Human oversight is always available when needed.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">7. Information Sharing</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We do not sell your personal information. We may share data with the following categories of service providers necessary for operations:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li><strong>Cloud Infrastructure:</strong> Amazon Web Services (hosting, databases, compute)</li>
                <li><strong>AI/ML Providers:</strong> OpenAI, Groq (language models); Deepgram (speech-to-text); Cartesia, Google Cloud TTS (text-to-speech)</li>
                <li><strong>Communication:</strong> Twilio, Telnyx (telephony and SMS); Daily.co (WebRTC)</li>
                <li><strong>Payments:</strong> Stripe (payment processing)</li>
                <li><strong>Caching:</strong> Redis Cloud (real-time data caching)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                We may also disclose information as required by law or to protect our rights.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">8. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement industry-standard security measures to protect your information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li>TLS encryption for all data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>API key authentication and OAuth 2.0 for third-party integrations</li>
                <li>Role-based access controls and audit logging</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                No system is 100% secure, and we cannot guarantee absolute security of your data.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">9. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We retain data for the following periods:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li><strong>Account data:</strong> Retained while your account is active and for 30 days after deletion.</li>
                <li><strong>Conversation transcripts:</strong> Retained for up to 90 days for quality assurance, then automatically deleted.</li>
                <li><strong>Voice recordings:</strong> Not persistently stored. Audio is processed in real-time and discarded.</li>
                <li><strong>Google user data:</strong> OAuth tokens are retained while the integration is active. Cached calendar and email data expires automatically within 24 hours. All Google data is deleted upon revocation of access.</li>
                <li><strong>Billing records:</strong> Retained as required by applicable tax and accounting regulations.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">10. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
                <li>Access, update, or delete your personal information.</li>
                <li>Revoke third-party service authorizations (including Google) at any time.</li>
                <li>Request a copy of your data in a portable format.</li>
                <li>Object to or restrict certain data processing activities.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                To exercise any of these rights, contact us at <a href="mailto:privacy@remote-assistant.ai" className="text-primary hover:underline">privacy@remote-assistant.ai</a>.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">11. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                Our liability is limited to the maximum extent permitted by law. We provide services "as is" without warranties.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">12. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of material changes by posting the updated policy on this page with a revised effective date. Your continued use of the service after changes constitutes acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-primary mb-4">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                For privacy questions or to exercise your data rights, contact us at:<br />
                <a href="mailto:privacy@remote-assistant.ai" className="text-primary hover:underline">privacy@remote-assistant.ai</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Privacy;